import React, { useState } from 'react';
import { X, Minus, Plus, Trash2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import { useAdminAuth } from '../admin/context/AdminAuthContext';
import getImageUrl from '../utils/getImageUrl';

const CartSidebar = () => {
  const {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    removeFromCart,
    updateQuantity,
    getTotalPrice,
    clearCart
  } = useCart();

  const { t, language } = useLanguage();
  const currencyContext = useCurrency();
  const { addOrder } = useAdminAuth();

  const [submitting, setSubmitting] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    address: '',
    email: '',
    notes: ''
  });

  if (!isCartOpen) return null;

  const formatPrice = (price) => {
    try {
      return currencyContext?.convertPrice ? currencyContext.convertPrice(price) : price;
    } catch (e) { return price; }
  };

  const getSymbol = () => {
    try {
      return currencyContext?.getCurrencySymbol ? currencyContext.getCurrencySymbol() : '₼';
    } catch (e) { return '₼'; }
  };

   const handleWhatsAppOrder = async () => {
     if (submitting) return;

     if (!customerInfo.name || !customerInfo.phone || !customerInfo.address) {
       alert('Zəhmət olmasa tələb olunan sahələri doldurun!');
       return;
     }

     // Pəncərəni İNDİ açırıq — istifadəçi jesti hələ aktivdir.
     // Sorğu bitəndən sonra içinə real ünvanı yazacağıq.
     const waWindow = window.open('', '_blank');

     setSubmitting(true);
     try {
       const orderPayload = {
         customerName: customerInfo.name,
         customerPhone: customerInfo.phone,
         customerEmail: customerInfo.email || 'client@laliel.az',
         deliveryAddress: customerInfo.address,
         notes: customerInfo.notes || 'WhatsApp sifarişi',
         items: cartItems.map(item => ({
           productId: Number(item.id || item.productId || item._id),
           quantity: Number(item.quantity) || 1,
           size: item.size || 'STD',
           color: item.color || 'Standard'
         }))
       };

       const savedOrder = await addOrder(orderPayload);

       const orderDetails = cartItems
         .map(i => `• ${typeof i.name === 'object' ? i.name[language] || i.name.az : i.name} | Ölçü: ${i.size} | Say: ${i.quantity}`)
         .join('\n');

       const orderRef = savedOrder?.orderNumber || savedOrder?.id || 'YENİ';
       const msg = `*Yeni Sifariş #${orderRef}*\n\n${orderDetails}\n\n*Cəmi: ${formatPrice(getTotalPrice())} ${getSymbol()}*\n\n*Müştəri:* ${customerInfo.name}\n*Tel:* ${customerInfo.phone}\n*Ünvan:* ${customerInfo.address}`;

       const waUrl = `https://wa.me/994999737000?text=${encodeURIComponent(msg)}`;

       if (waWindow && !waWindow.closed) {
         // Əvvəlcədən açılmış pəncərəni yönləndiririk
         waWindow.location.href = waUrl;
       } else {
         // Pəncərə bloklanıbsa — cari tabda aç
         window.location.href = waUrl;
       }

       clearCart();
       setIsCartOpen(false);
     } catch (e) {
       if (waWindow && !waWindow.closed) waWindow.close();
       const serverMsg = e?.response?.data?.error;
       alert(serverMsg || 'Sifariş zamanı xəta baş verdi.');
     } finally {
       setSubmitting(false);
     }
   };



  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsCartOpen(false)}></div>
      <div className="fixed right-0 top-0 h-full w-full md:w-[450px] bg-white shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-serif">{t('cart') || 'Səbət'} ({cartItems.length})</h2>
          <button onClick={() => setIsCartOpen(false)} className="p-2 hover:bg-gray-100 rounded-full cursor-pointer"><X className="w-6 h-6" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {cartItems.length === 0 ? (
            <p className="text-center text-gray-500 mt-8">Səbətiniz boşdur</p>
          ) : (
            cartItems.map((item, index) => {
              const itemId = item.id || item.productId || item._id;
              const itemName = typeof item.name === 'object' ? item.name[language] || item.name.az : item.name;

              return (
                <div key={`${itemId}-${index}`} className="flex gap-4 border-b pb-4 items-center">
                  <img
                    src={getImageUrl(item)}
                    alt={itemName}
                    className="w-20 h-20 object-cover rounded bg-gray-100 flex-shrink-0"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/100x100?text=No+Image';
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium mb-1 truncate">{itemName}</h3>
                    <p className="text-sm text-gray-500">Ölçü: {item.size || 'STD'}</p>
                    <p className="text-sm font-medium mt-1">{formatPrice(item.price)} {getSymbol()}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <button onClick={() => removeFromCart(itemId)} className="text-red-500 hover:text-red-700 cursor-pointer">
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-2 border rounded">
                      <button onClick={() => updateQuantity(itemId, item.quantity - 1)} className="p-1 hover:bg-gray-100 cursor-pointer"><Minus className="w-3 h-3" /></button>
                      <span className="px-2 text-sm">{item.quantity}</span>
                      <button onClick={() => updateQuantity(itemId, item.quantity + 1)} className="p-1 hover:bg-gray-100 cursor-pointer"><Plus className="w-3 h-3" /></button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {cartItems.length > 0 && (
          <div className="border-t p-6 space-y-3 bg-white">
            <h3 className="font-medium text-sm">Müştəri Məlumatları</h3>
            <input type="text" className="w-full border p-2.5 text-sm rounded focus:outline-none focus:border-black" placeholder="Ad Soyad" value={customerInfo.name} onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})} />
            <input type="tel" className="w-full border p-2.5 text-sm rounded focus:outline-none focus:border-black" placeholder="Telefon (+994...)" value={customerInfo.phone} onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})} />
            <textarea className="w-full border p-2.5 text-sm rounded focus:outline-none focus:border-black" placeholder="Çatdırılma ünvanı" rows="2" value={customerInfo.address} onChange={(e) => setCustomerInfo({...customerInfo, address: e.target.value})}></textarea>
            <div className="flex justify-between items-center py-2 border-t border-b font-medium">
              <span>Cəmi:</span>
              <span className="text-xl font-serif">{formatPrice(getTotalPrice())} {getSymbol()}</span>
            </div>
            <button
              onClick={handleWhatsAppOrder}
              disabled={submitting}
              className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Göndərilir...' : 'WhatsApp ilə Sifariş Et'}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartSidebar;